import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, X, Sparkles } from 'lucide-react';

export function VideoPosterModal({
  title = "Guruji's Sacred Anugraha Bhashanam",
  subtitle = "Watch Guruji explain the profound power of 27 Nakshatra Homam",
  posterUrl = "/images/sai_sahasranama_ayushya_homam_banner.jpg",
  videoEmbedUrl = process.env.REACT_APP_PROMO_VIDEO_URL || "/videos/promo_video_01.mp4",
  aspectRatio = "16/9",
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeVideoSrc, setActiveVideoSrc] = useState(videoEmbedUrl);

  useEffect(() => {
    setActiveVideoSrc(videoEmbedUrl || "/videos/promo_video_01.mp4");
  }, [videoEmbedUrl]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Helper to convert any YouTube URL into an embeddable URL
  const formatVideoSrc = (url) => {
    if (!url) return { type: 'video', src: '/videos/promo_video_01.mp4' };
    
    // Check if it is a direct video file (mp4, webm, ogg, etc.)
    const isDirectVideo = /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url) || url.startsWith('/videos/');
    if (isDirectVideo) {
      return { type: 'video', src: url };
    }

    // Handle YouTube URLs
    let videoId = "";
    if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0]?.split("&")[0];
    } else if (url.includes("youtube.com/watch")) {
      const match = url.match(/[?&]v=([^&]+)/);
      videoId = match ? match[1] : "";
    } else if (url.includes("youtube.com/shorts/")) {
      videoId = url.split("youtube.com/shorts/")[1]?.split("?")[0]?.split("&")[0];
    } else if (url.includes("youtube.com/embed/") || url.includes("youtube-nocookie.com/embed/")) {
      return { type: 'iframe', src: url.includes("autoplay") ? url : `${url}${url.includes("?") ? "&" : "?"}autoplay=1` };
    }

    if (videoId) {
      return {
        type: 'iframe',
        src: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`
      };
    }

    // Default fallback
    return { type: 'video', src: url };
  };

  const videoSource = formatVideoSrc(activeVideoSrc);

  const modalContent = isOpen && typeof document !== 'undefined' ? (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => setIsOpen(false)}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999999,
        background: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        boxSizing: 'border-box'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 860,
          background: '#0a192f',
          borderRadius: 20,
          border: 'none',
          padding: 16,
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9)'
        }}
      >
        {/* Modal Topbar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 12,
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)'
          }}
        >
          <h4 style={{ color: '#ffffff', margin: 0, fontSize: 15, fontWeight: 700 }}>
            {title}
          </h4>
          <button
            onClick={() => setIsOpen(false)}
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video Player */}
        <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 14, overflow: 'hidden', marginTop: 12, background: '#000000' }}>
          {videoSource.type === 'video' ? (
            <video
              src={videoSource.src}
              controls
              autoPlay
              playsInline
              preload="auto"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              onError={(e) => {
                if (!e.target.src.includes('/videos/promo_video_01.mp4')) {
                  setActiveVideoSrc('/videos/promo_video_01.mp4');
                }
              }}
            />
          ) : (
            <iframe
              src={videoSource.src}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
            />
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div style={{ marginTop: 16 }}>
      {/* Video Poster Trigger Card */}
      <div
        onClick={() => setIsOpen(true)}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 24,
          border: '2px solid rgba(245, 197, 66, 0.8)',
          background: '#0a192f',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(184, 134, 11, 0.12)',
          aspectRatio: aspectRatio === '16/9' ? '16/9' : '4/3',
          transition: 'all 0.3s ease'
        }}
      >
        <img
          src={posterUrl}
          alt={title}
          onError={(e) => {
            e.target.src = '/images/sai_sahasranama_ayushya_homam_banner.jpg';
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transition: 'transform 0.5s ease'
          }}
        />

        {/* Ambient Dark Overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(10, 25, 47, 0.95) 0%, rgba(10, 25, 47, 0.4) 50%, transparent 100%)'
          }}
        />

        {/* Center Pulsing Play Button */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f5c542 0%, #d4a017 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 25px rgba(245, 197, 66, 0.6)',
              border: '2px solid rgba(255, 255, 255, 0.6)',
              cursor: 'pointer'
            }}
          >
            <Play className="w-7 h-7" style={{ fill: '#0a192f', color: '#0a192f', marginLeft: 3 }} />
          </div>
        </div>

        {/* Bottom Title Bar */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            insetInline: 0,
            padding: '14px 18px',
            textAlign: 'left'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#f5c542',
              marginBottom: 2
            }}
          >
            <Sparkles className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
            <span>Divine Video Message</span>
          </div>
          <h4
            style={{
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 700,
              margin: '0 0 2px 0'
            }}
          >
            {title}
          </h4>
          {subtitle && (
            <p
              style={{
                color: '#cbd5e1',
                fontSize: 11.5,
                margin: 0
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Render Modal via Portal directly to body */}
      {modalContent && createPortal(modalContent, document.body)}
    </div>
  );
}

export default VideoPosterModal;
